<template>
  <Card v-if="!showErrorTile" :loadingState="loading">
    <template #content>
      <Collapser
        id="motor.toggle-details"
        :collapserTitle="$t('motorDetails.collapserTitle')"
        :expanded="true"
        trackId="toggleDetails"
      >
        <template v-slot:collapserContent>
          <div v-for="(dataBlock, idx) in databoxData" :key="idx">
            <DataBox
              :data="[dataBlock]"
              action-link-icon-class="icon-reload"
              link-type="link2"
              @click:action-link="handleActionLink"
              @click:icon="clickEvent"
            ></DataBox>
          </div>
        </template>
      </Collapser>
    </template>
  </Card>

  <ErrorTile v-if="showErrorTile" :title="$t('motorDetails.errorTitle')"></ErrorTile>

  <Modal
    title="Hinweis zum amtlichen Kennzeichen"
    :showModal="showLicensePlateModal"
    :centeredText="false"
    :maxWidth="600"
    :showCloseIcon="true"
    @closeModal="closeLicensePlateModal"
  >
    <template v-slot:content>
      <div v-dompurify-html="$t('motorDetails.licensePlateModal')"></div>
    </template>
  </Modal>

  <Modal
    :showModal="showLexExtrasModal"
    :title="extraLexModalTitle"
    :centeredText="false"
    :maxWidth="600"
    :showCloseIcon="true"
    :headerDivider="false"
    @closeModal="closeLexExtraModal"
  >
    <template #content>
      <div v-dompurify-html="lexExtraModalContent"></div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, type Ref } from 'vue'
import { Card, Collapser, type DataBlock, DataBox, Modal } from 'de-zurich-ui-library'
import ErrorTile from '@/components/common/ErrorTile.vue'
import { navigate } from '@/utils/NavigationUtils'
import { useI18n } from 'vue-i18n'
import { INSURED_VEHICLE_ACTION_LINK_ID } from '@/utils/motor/MotorDataBoxDataUtils'
import { useMotorStore } from '@/stores/motorStore'
import { usePoliciesStore } from '@/stores/policiesStore'
import { type MotorContract } from '@/types/motor/MotorContract'
import { type EMobilContract, isEMobilContract } from '@/types/emobil/EMobilContract'
import type { Policy } from 'policies-api'
import { MotorDetailsFactory } from '@/components/motor/MotorDetailsFactory'

const { t } = useI18n()

const props = defineProps({
  policyId: {
    type: String
  },
  isQuote: {
    type: Boolean,
    default: false
  },
  quoteId: {
    type: String
  },
  partnerAddressIdProp: {
    type: String
  }
})

const vehicleChangeUrl = globalThis.ENV_CONFIG?.REDIRECTS.KFZ_VEHICLE_CHANGE

const loading = ref(true)
const showErrorTile = ref(false)
const showLicensePlateModal = ref(false)
const showLexExtrasModal = ref(false)
const lexExtraModalKey = ref('')
const lexExtraModalType = ref<'liability' | 'comprehensive' | ''>('')
const lexExtraModalContent = ref('')
const partnerAddressId = ref('')

const databoxData: Ref<DataBlock[]> = ref([])
const contract: Ref<MotorContract | null> = ref(null)

onMounted(async () => {
  loading.value = true
  showErrorTile.value = false

  let response: MotorContract | EMobilContract | null = null
  try {
    response = props.isQuote
      ? await useMotorStore().getMotorQuoteByQuoteId(props.quoteId ?? '')
      : await useMotorStore().getMotorContractByPolicyId(props.policyId ?? '')
  } catch (error) {
    handleLoadError(error)
    return
  }

  if (!response) {
    doShowErrorTile()
    return
  }

  if (isEMobilContract(response)) {
    return
  }

  contract.value = response

  prepareDataboxData()

  if (props.partnerAddressIdProp || props.isQuote) {
    partnerAddressId.value = props.partnerAddressIdProp ?? ''
  } else if (props.policyId) {
    let policy: Policy | null = null
    try {
      policy = await usePoliciesStore().getPolicyByPolicyId(props.policyId)
    } catch (error) {
      handleLoadError(error)
    }

    if (!policy) {
      doShowErrorTile()
      return
    }

    partnerAddressId.value = policy.partnerAddressIDVN?.toString() ?? ''
  }

  loading.value = false
})

function handleLoadError(error: any) {
  loading.value = false

  if (error.response && error.response.status === 401) {
    // Unauthenticated call
    // redirect to login page
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
  } else {
    doShowErrorTile()
  }
}

function doShowErrorTile() {
  contract.value = null
  showErrorTile.value = true
}

function clickEvent(href: string): void {
  if (href === 'openModal_licensePlate') {
    showLicensePlateModal.value = true
  } else if (href.startsWith('openModal_liabilityExtra_')) {
    lexExtraModalType.value = 'liability'
    lexExtraModalKey.value = href.replaceAll('openModal_liabilityExtra_', '')
  } else if (href.startsWith('openModal_comprehensiveExtra_')) {
    lexExtraModalType.value = 'comprehensive'
    lexExtraModalKey.value = href.replaceAll('openModal_comprehensiveExtra_', '')
  }
  if (
    href.startsWith('openModal_liabilityExtra_') ||
    href.startsWith('openModal_comprehensiveExtra_')
  ) {
    // Set modal content directly (long -> short -> empty)
    const key = lexExtraModalKey.value
    const shortKey = 'motorDetails.extras.descriptions.' + key
    const shortText = t(shortKey) as string
    lexExtraModalContent.value = shortText && shortText !== shortKey ? shortText : ''
    showLexExtrasModal.value = true
  }
}

function closeLicensePlateModal(): void {
  showLicensePlateModal.value = false
}

function closeLexExtraModal(): void {
  showLexExtrasModal.value = false
  lexExtraModalKey.value = ''
  lexExtraModalType.value = ''
  lexExtraModalContent.value = ''
}

const extraLexModalTitle = computed(() => {
  if (!lexExtraModalKey.value || !lexExtraModalType.value) return ''
  return t(
    `motorDetails.extras.${
      lexExtraModalType.value === 'liability' ? 'liability' : 'comprehensive'
    }.${lexExtraModalKey.value}`
  )
})

// extraLexModalContent now set directly inside clickEvent branches

function handleActionLink(actionLinkId: string) {
  if (actionLinkId === INSURED_VEHICLE_ACTION_LINK_ID) {
    let finalVehicleChangeUrl: string = vehicleChangeUrl
    if (props.policyId) {
      finalVehicleChangeUrl = finalVehicleChangeUrl.replaceAll(
        '<POLICY_ID>',
        props.policyId
      )
    }
    finalVehicleChangeUrl = finalVehicleChangeUrl
      .replaceAll('<PARTNER_ADDRESS_ID>', partnerAddressId.value)
      .slice(1)

    navigate(finalVehicleChangeUrl)
  }
}

function prepareDataboxData(): void {
  // clear previous databoxData
  databoxData.value = []
  const factory: MotorDetailsFactory = new MotorDetailsFactory(props.isQuote)
  factory.createDetails(contract.value, databoxData.value)
}
</script>

<style scoped lang="scss">
.is-styled .list__wrapper div.hoverable-icon.hoverable-icon-span {
  display: flex;
}

:deep(.icon-motor-details-vorteile) {
  font-size: 30px !important;
}

// Fix for Zusatzabsicherung/LEX section: prevent text overflow and ensure wrapping
// KPTWO-6421: Fix mobile LEX text wrapping and spacing to info icon
:deep(.list__term) {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  hyphens: auto;
  overflow: visible;
  min-height: auto;
  box-sizing: border-box;

  // Default: full width when no icon is present
  max-width: 100%;
}

// When icon is present in the same wrapper, reduce term width
:deep(.list__wrapper:has(.hoverable-icon-span)) {
  .list__term {
    max-width: calc(100% - 50px);
    padding-right: 8px;
  }
}

// Specifically target full-width terms - only reduce if icon present
:deep(.list__term--full) {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  overflow: visible;
  box-sizing: border-box;

  // Default: full width
  max-width: 100%;
}

// Ensure info icon doesn't get overlapped
:deep(.hoverable-icon-span) {
  flex-shrink: 0;
  margin-left: auto;
  min-width: 40px;
}

// Mobile-specific fixes for LEX section with info icons
@media (max-width: 599px) {
  :deep(.list__term) {
    // Ensure proper wrapping on mobile
    line-height: 1.4;
  }

  // Only add spacing when icon is present
  :deep(.list__wrapper:has(.hoverable-icon-span)) {
    .list__term {
      padding-right: 45px;
      max-width: calc(100% - 50px);
    }
  }

  // Ensure info icon has proper spacing on mobile
  :deep(.hoverable-icon-span) {
    margin-left: 8px;
    flex-shrink: 0;
    min-width: 40px;
  }
}
</style>
